/**
 * Os tipos do motor CPTu: o que entra (leituras e premissas) e o que sai
 * (o `Resultado`, uma coluna por grandeza). Spec 07 §5, RN-252.
 *
 * O motor é colunar: cada grandeza é um `Float64Array` com uma posição por
 * leitura, na ordem da profundidade. «Não se aplica» é NaN, nunca 0 nem −1;
 * as zonas (SBT, SBTn, SBTn modificado, Schneider) são `Int8Array` e usam 0
 * para «sem classe».
 *
 * As unidades de cada grandeza estão em `unidades.ts`.
 */
import { GAMA_W_PADRAO } from './unidades';

/** Uma coluna de números, uma posição por leitura. */
export type Serie = Float64Array;

/**
 * As leituras do cone, já em unidades de trabalho: profundidade em m, qc em
 * MPa, fs e u₂ em kPa. Inclinação (°), velocidade (cm/s) e tempo (s) vêm do
 * arquivo do equipamento e só servem à conferência.
 */
export interface Leituras {
  profundidade: Serie;
  qc: Serie;
  fs: Serie;
  u2: Serie;
  inclinacao?: Serie;
  velocidade?: Serie;
  tempo?: Serie;
}

/**
 * As premissas de uma interpretação: tudo o que o engenheiro pode mudar sem
 * tocar no bruto. Os valores de `PREMISSAS_PADRAO` são os do `.cpt` do 2609350.
 */
export interface Premissas {
  /** Razão de área líquida do cone, a (0,80 no cone de 10 cm²). */
  a: number;
  /** Peso específico da água, kN/m³. */
  gamaW: number;
  /** Profundidade do nível d'água, m. */
  na: number;
  /** Cota da boca da vertical, m (null se não informada). */
  cota: number | null;
  /**
   * Profundidade do pré-furo, m (0 = sem pré-furo). Sem efeito no motor 1.0.0: a primeira leitura
   * leva o peso da coluna acima com ou sem pré-furo (σv₁ = γ₁·z₁, `basico.ts`), e tirar o trecho
   * do pré-furo é um corte da edição (`edicao.ts`). Fica no contrato para a tela e o relatório.
   */
  preFuro: number;
  /**
   * u₀ hidrostático a partir do NA, ou por pontos (perfil não hidrostático). O gabarito só exercita
   * o hidrostático: com `'pontos'`, o que depende de u₀ fica fora da homologação (`conformidade.ts`).
   */
  u0Modo: 'hidrostatico' | 'pontos';
  /**
   * Pontos profundidade (m) × u₀ (kPa) do perfil por pontos. Lista só de leitura: a do padrão é
   * congelada, e `premissasEfetivas` devolve uma nova a cada chamada.
   */
  u0Pontos: readonly { z: number; u0: number }[];
  /**
   * γ automático (Robertson & Cabal 2010) ou fixo. O gabarito só exercita o automático: com
   * `'fixo'`, o que depende de γ fica fora da homologação.
   */
  gamaModo: 'robertson2010' | 'fixo';
  /** γ fixo, kN/m³ — também a reserva do γ automático onde a fórmula não dá número (`basico.ts`). */
  gamaFixo: number;
  /**
   * Aplica o piso ao γ automático (γ ≥ 1,4·γw, `basico.ts`). O gabarito só exercita o piso ligado:
   * com `false`, o que depende de γ fica fora da homologação.
   */
  gamaLimitar: boolean;
  /**
   * Corte de Cn (1,70 quando ligado); null = sem corte. O gabarito só exercita o sem corte: com um
   * corte, a normalização e o que vem dela ficam fora da homologação.
   */
  cnCorte: number | null;
  /**
   * Ic de corte, que separa o comportamento de argila e de areia (2,60). Sem efeito no motor
   * 1.0.0 — ver `correlacoes.ts` (a aplicabilidade segue a zona SBTn).
   */
  icCorte: number;
  /**
   * ΔIc (padrão 0), do `.cpt`. Sem efeito no motor 1.0.0 — ver `correlacoes.ts`; por isso a
   * homologação só vale com 0 (com outro valor o CPeT-IT daria outro resultado, e o motor o mesmo).
   */
  deltaIc: number;
  /**
   * Janela da média móvel de qc, fs e u₂, em leituras, que o básico aplica
   * antes das contas, como o CPeT-IT (o gabarito 2609350 só fecha com ela: ver
   * `basico.ts`, regra bas.media-movel-nas-contas); 1 = sem média. As colunas
   * qc, fs e u₂ continuam as leituras. O gabarito só exercita a janela de 3:
   * com outra, o que muda com ela fica fora da homologação (`conformidade.ts`).
   */
  mediaMovel: number;
  /** Nkt de Su. */
  nkt: number;
  /**
   * Nkt calculado por Fr em vez de fixo. O gabarito só exercita o fixo: com `true`, Nkt, Su, Su/σ'v
   * e a resistência ficam fora da homologação (`condicoesAtendidas`, `conformidade.ts`).
   */
  nktPorFr: boolean;
  /** Ns da sensibilidade, St = Ns / Fr. */
  ns: number;
  /** Nu (padrão 7), usado quando Su sai por Nu. */
  nu: number;
  /**
   * Calcula Su por Nu em vez de Nkt. O gabarito só exercita o Nkt: com `true`, Su, Su/σ'v e a
   * resistência ficam fora da homologação.
   */
  suPorNu: boolean;
  /** kOCR (Mayne), 0,33 no `.cpt`. */
  kocr: number;
  /**
   * kOCR automático em vez do informado. O gabarito só exercita o informado: com `true`, kOCR, OCR
   * e K₀ ficam fora da homologação.
   */
  kocrAutomatico: boolean;
  /** Constante CDr da densidade relativa. */
  cdr: number;
  /** φ'cv, graus. */
  phiCv: number;
  /**
   * φ' de pico padrão, graus, onde o φ' de pico não se aplica (Bq fora de 0,1–1): entra no K₀ das
   * argilosas. É o `Default_Peak_Phi` do `.cpt` (0 no 2609350, o que dá K₀ = 1) — comportamento
   * copiado do CPeT-IT, sem fonte publicada (`correlacoes.ts`, escolha 8). `null`: sem φ' de pico,
   * sem K₀ (NaN).
   */
  phiPicoPadrao: number | null;
  /** Limite de Qtn,cs (o «Qtn,cs limit» do `.cpt`). Sem efeito no motor 1.0.0 — ver `correlacoes.ts`. */
  qtncsLimite: number;
  /** Q (padrão 10), do `.cpt`. Sem efeito no motor 1.0.0 — ver `correlacoes.ts`. */
  q: number;
  /**
   * Grau de carga de Es, fração (0,25 = 25 %). Sem efeito no motor 1.0.0 — ver `correlacoes.ts`
   * (o Es é a fórmula publicada, sem fator).
   */
  esGrauCarga: number;
  /** α(M) máximo. */
  alfaMMax: number;
  /** Densidade relativa dos grãos, Gs. */
  gs: number;
  /** T*₅₀ da dissipação (0,245 para u₂). */
  t50Fator: number;
  /** Raio do cone, m (o do `.cpt`: 0,0183). */
  raioCone: number;
}

/**
 * O padrão Testesolo, igual ao do `.cpt` do 2609350. Congelado, com a lista de pontos de u₀: quem
 * quer mudar uma premissa pede uma cópia a `premissasEfetivas`, que devolve um objeto novo e mutável.
 */
export const PREMISSAS_PADRAO: Readonly<Premissas> = Object.freeze({
  a: 0.8,
  gamaW: GAMA_W_PADRAO,
  na: 0,
  cota: null,
  preFuro: 0,
  u0Modo: 'hidrostatico',
  u0Pontos: Object.freeze([]),
  gamaModo: 'robertson2010',
  gamaFixo: 19,
  gamaLimitar: true,
  cnCorte: null,
  icCorte: 2.6,
  deltaIc: 0,
  mediaMovel: 3,
  nkt: 14,
  nktPorFr: false,
  ns: 7,
  nu: 7,
  suPorNu: false,
  kocr: 0.33,
  kocrAutomatico: false,
  cdr: 350,
  phiCv: 32,
  phiPicoPadrao: 0,
  qtncsLimite: 100,
  q: 10,
  esGrauCarga: 0.25,
  alfaMMax: 8,
  gs: 2.65,
  t50Fator: 0.245,
  raioCone: 0.0183,
} satisfies Premissas);

const CHAVES_DAS_PREMISSAS = Object.keys(PREMISSAS_PADRAO) as (keyof Premissas)[];

/** As premissas cujo tipo aceita `null`. */
type PremissaAnulavel = { [K in keyof Premissas]: null extends Premissas[K] ? K : never }[keyof Premissas];

/**
 * As premissas em que `null` é um valor: cota desconhecida, sem corte de Cn, sem φ' de pico
 * padrão. O tipo obriga a lista a ter todas elas e só elas.
 */
const ANULAVEIS: Record<PremissaAnulavel, true> = { cota: true, cnCorte: true, phiPicoPadrao: true };

function sobrepor(destino: Premissas, origem: Partial<Premissas> | undefined): void {
  if (!origem) return;
  const alvo = destino as unknown as Record<string, unknown>;
  for (const chave of CHAVES_DAS_PREMISSAS) {
    const valor = origem[chave];
    // `undefined` não apaga; `null` só é valor onde a premissa aceita null — num número, num modo
    // ou numa lista, o `null` de um JSON do banco não apaga o que vinha de baixo.
    if (valor === undefined) continue;
    if (valor === null && !Object.prototype.hasOwnProperty.call(ANULAVEIS, chave)) continue;
    alvo[chave] = valor;
  }
}

/**
 * As premissas que valem para uma vertical: o padrão, coberto pelas da
 * campanha, cobertas pelas da vertical. `undefined` não sobrescreve, e `null`
 * só sobrescreve `cota`, `cnCorte` e `phiPicoPadrao`; chaves que não são
 * premissas são ignoradas. Devolve sempre um objeto novo e mutável (e uma lista
 * de pontos de u₀ nova): quem altera o resultado não altera o padrão, que é
 * congelado.
 */
export function premissasEfetivas(campanha: Partial<Premissas>, vertical?: Partial<Premissas>): Premissas {
  const efetivas: Premissas = { ...PREMISSAS_PADRAO };
  sobrepor(efetivas, campanha);
  sobrepor(efetivas, vertical);
  // Um ponto nulo ou uma lista que não é lista (um JSON do banco) não derruba a vertical: o básico
  // ignora o ponto sem número, e sem lista vale a lista vazia.
  efetivas.u0Pontos = Array.isArray(efetivas.u0Pontos)
    ? efetivas.u0Pontos.map((p) => ({ z: p?.z, u0: p?.u0 }))
    : [];
  return efetivas;
}

/**
 * Tudo o que o motor calcula, uma coluna por grandeza (as colunas «Basic» e
 * «Estimations» do CPeT-IT mais N1(60)).
 */
export type Grandeza =
  | 'z' | 'qc' | 'fs' | 'u2' | 'qt' | 'rf' | 'gama' | 'sv' | 'u0' | 'svl'
  | 'qt1' | 'fr' | 'bq' | 'n' | 'cn' | 'ic' | 'qtn' | 'icSbt' | 'U2' | 'ib'
  | 'k' | 'ch' | 'n60' | 'n160' | 'm' | 'dr' | 'phi' | 'es' | 'g0' | 'nkt'
  | 'su' | 'suRazao' | 'kocr' | 'ocr' | 'vs' | 'psi' | 'k0' | 'st' | 'phiPico'
  | 'resistencia' | 'kc' | 'qtncs' | 'suR' | 'w' | 'e' | 'gamaD' | 'porosidade';

/** A interpretação de uma vertical. */
export interface Resultado {
  /** A versão do motor que calculou (`MOTOR_CPTU_VERSAO`). */
  versao: string;
  /** Quantas leituras entraram nas contas (depois da edição). */
  n: number;
  col: Record<Grandeza, Serie>;
  /** Zonas SBT (não normalizado), 0 a 9. */
  sbt: Int8Array;
  /** Zonas SBTn (normalizado), 0 a 9. */
  sbtn: Int8Array;
  /** SBTn modificado (2016). */
  sbtnMod: Int8Array;
  /** Classificação de Schneider (2008). */
  schneider: Int8Array;
  /** Leituras tiradas pela edição (picos, cortes), com o motivo. */
  removidas: { ordem: number; z: number; motivo: string }[];
  /** Avisos em português para quem lê o resultado. */
  avisos: string[];
  /**
   * As premissas efetivas com que a vertical foi calculada (o padrão coberto pelas da campanha e
   * pelas da vertical), numa cópia nova: é o que se passa a `grandezaHomologada(g, r.premissas)` e a
   * `dissipacaoHomologada(s, r.premissas)`, sem refazer a mescla.
   */
  premissas: Premissas;
}

/**
 * Um comportamento do motor sem fonte publicada — copiado do CPeT-IT ou escolhido pelo motor por
 * analogia — que o RT assina na homologação (RN-252). Cada arquivo do motor que tem um exporta a
 * lista dos seus (`REGRAS_A_ASSINAR_*`), e o comentário do código leva o rótulo com o mesmo `id`
 * entre colchetes; a conformidade (`conformidade.ts`) leva a lista inteira ao RT.
 */
export interface RegraAAssinar {
  /**
   * `bas.…`, `cla.…`, `cor.…`, `dis.…` ou `cam.…` — o rótulo no comentário do código. As `cam.…` (as
   * sugestões de camadas, `camadas.ts`, onda 313) são sugestão, fora do RN-252: vão ao RT à parte, não
   * pela conformidade.
   */
  id: string;
  /** O arquivo do motor onde a regra está. */
  arquivo: string;
  /** O que o motor faz. */
  descricao: string;
  /** Por que: a fração das linhas do gabarito com a regra e sem ela, ou a observação que a sustenta. */
  evidencia: string;
}
